/** biome-ignore-all lint/correctness/useExhaustiveDependencies: animation lifecycle refs */
"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { sendGTMEvent } from "@next/third-parties/google";
import Image from "next/image";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { usePageTransition } from "@/components/page-transition-provider";
import { loadGsap, shouldReduceMotion } from "@/lib/gsap";
import { getBudgetOptionsForType, type ProposalFormValues, proposalFormSchema, proposalSteps } from "@/lib/proposal";
import { ProposalActionButton } from "./proposal-action-button";
import { sectionStepTitles } from "./proposal-form-constants";
import { ProposalStepBudget } from "./proposal-step-budget";
import { ProposalStepCompany } from "./proposal-step-company";
import { ProposalStepDetails } from "./proposal-step-details";
import { ProposalStepPersonal } from "./proposal-step-personal";
import { ProposalStepType } from "./proposal-step-type";
import { ProposalStepper } from "./proposal-stepper";
const stepConversionLabels: Record<string, string[]> = {
  personal: ["0aroCPa_vLYZEMTs5NMD", "4aKsCJf7xbYZEMTs5NMD", "GHcECLSAxrYZEMTs5NMD"],
  company: ["JP_hCJ-swLYZEMTs5NMD"],
  type: ["tqI2CMuFxrYZEMTs5NMD"],
  budget: ["3L5zCNi1wLYZEMTs5NMD"],
  details: ["agcWCLHjvLYZEMTs5NMD"]
};
function sendStepConversion(stepKey: string) {
  const labels = stepConversionLabels[stepKey];
  if (!labels?.length) return;
  for (const label of labels) {
    sendGTMEvent({
      event: "conversion",
      value: {
        send_to: `AW-981022276/${label}`
      }
    });
  }
}
const trackingKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
function readTrackingData() {
  if (typeof window === "undefined") {
    return {};
  }
  const fallbackOrigin = sessionStorage.getItem("origin") ?? "Site Flowlab";
  return {
    origin: fallbackOrigin,
    utm_source: sessionStorage.getItem("utm_source") ?? "",
    utm_medium: sessionStorage.getItem("utm_medium") ?? "",
    utm_campaign: sessionStorage.getItem("utm_campaign") ?? "",
    utm_content: sessionStorage.getItem("utm_content") ?? "",
    utm_term: sessionStorage.getItem("utm_term") ?? ""
  };
}
export function ProposalForm(t0) {
  const $ = _c(77);
  const {
    initialStep: t1
  } = t0;
  const initialStep = t1 === undefined ? 0 : t1;
  const {
    navigate
  } = usePageTransition();
  const [activeStep, setActiveStep] = useState(initialStep);
  const [submitError, setSubmitError] = useState(null);
  const sectionRef = useRef(null);
  const asideRef = useRef(null);
  const asideLogoRef = useRef(null);
  const asideNavRef = useRef(null);
  const asideArtRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const stepContentRef = useRef(null);
  const actionRowRef = useRef(null);
  const errorRef = useRef(null);
  const hasMountedStepAnimationRef = useRef(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      budget: "",
      colaborators: t2,
      company: "",
      email: "",
      name: "",
      note: "",
      phone: "",
      types: []
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      defaultValues: t3,
      mode: "onTouched",
      reValidateMode: "onChange",
      resolver: zodResolver(proposalFormSchema)
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    clearErrors,
    control,
    formState: t5,
    handleSubmit,
    register,
    setValue,
    trigger
  } = useForm(t4);
  const {
    errors,
    isSubmitting,
    isValid
  } = t5;
  const watchedType = useWatch({
    control,
    name: "types"
  })?.[0];
  let t6;
  if ($[3] !== control) {
    t6 = {
      control,
      name: "budget"
    };
    $[3] = control;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const watchedBudget = useWatch(t6);
  let t7;
  if ($[5] !== watchedType) {
    t7 = getBudgetOptionsForType(watchedType);
    $[5] = watchedType;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const budgetOptions = t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  useEffect(_temp, t8);
  let t10;
  let t9;
  if ($[8] !== budgetOptions || $[9] !== clearErrors || $[10] !== setValue || $[11] !== watchedBudget) {
    t9 = () => {
      if (watchedBudget && !budgetOptions.includes(watchedBudget)) {
        setValue("budget", "", {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false
        });
        clearErrors("budget");
      }
    };
    t10 = [budgetOptions, clearErrors, setValue, watchedBudget];
    $[8] = budgetOptions;
    $[9] = clearErrors;
    $[10] = setValue;
    $[11] = watchedBudget;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[14] !== activeStep) {
    t11 = () => {
      const mobileActive = mobileHeaderRef.current?.querySelector(`[data-step-index="${activeStep}"]`);
      if (!(mobileActive instanceof HTMLElement)) {
        return;
      }
      mobileActive.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center"
      });
    };
    t12 = [activeStep];
    $[14] = activeStep;
    $[15] = t11;
    $[16] = t12;
  } else {
    t11 = $[15];
    t12 = $[16];
  }
  useEffect(t11, t12);
  const getOffscreenRight = _temp2;
  let t13;
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      if (shouldReduceMotion()) {
        return;
      }
      let cleanup;
      let cancelled = false;
      const setupAnimation = async function setupAnimation() {
        const gsap = await loadGsap();
        if (cancelled) {
          return;
        }
        const ctx = gsap.context(() => {
          const mobileHeader = mobileHeaderRef.current;
          const aside = asideRef.current;
          const asideLogo = asideLogoRef.current;
          const asideNav = asideNavRef.current;
          const asideArt = asideArtRef.current;
          const eyebrow = eyebrowRef.current;
          const title = titleRef.current;
          const step = stepContentRef.current;
          const actionRow = actionRowRef.current;
          if (!eyebrow || !title || !step) {
            return;
          }
          const asideItems = asideNav?.querySelectorAll("button") ?? [];
          const mobileItems = mobileHeader?.querySelectorAll("button") ?? [];
          const stepItems = step.querySelectorAll("[data-proposal-animate]");
          if (aside) {
            gsap.set(aside, {
              opacity: 0,
              x: -64
            });
          }
          if (asideLogo) {
            gsap.set(asideLogo, {
              opacity: 0,
              x: -28,
              y: 18
            });
          }
          if (asideNav) {
            gsap.set(asideItems, {
              opacity: 0,
              x: -26
            });
          }
          if (asideArt) {
            gsap.set(asideArt, {
              opacity: 0,
              scale: 1.08,
              x: -24,
              y: 36
            });
          }
          if (mobileHeader) {
            gsap.set([mobileHeader.querySelector("img"), mobileItems], {
              opacity: 0,
              y: 20
            });
          }
          const offscreenRight = getOffscreenRight();
          const actionRowItems = actionRow ? [actionRow] : [];
          gsap.set([eyebrow, title], {
            opacity: 0,
            x: offscreenRight
          });
          gsap.set(stepItems, {
            opacity: 0,
            x: offscreenRight,
            willChange: "transform,opacity"
          });
          if (actionRow) {
            gsap.set(actionRow, {
              opacity: 0,
              y: -260,
              willChange: "transform,opacity"
            });
          }
          const tl = gsap.timeline({
            defaults: {
              ease: "power3.out"
            }
          });
          if (aside) {
            tl.to(aside, {
              duration: 0.8,
              opacity: 1,
              x: 0
            }, 0);
          }
          if (asideLogo) {
            tl.to(asideLogo, {
              duration: 0.6,
              opacity: 1,
              x: 0,
              y: 0
            }, 0.16);
          }
          if (asideNav) {
            tl.to(asideItems, {
              duration: 0.52,
              opacity: 1,
              stagger: 0.08,
              x: 0,
              clearProps: "opacity,transform"
            }, 0.24);
          }
          if (asideArt) {
            tl.to(asideArt, {
              duration: 1,
              opacity: 1,
              scale: 1,
              x: 0,
              y: 0
            }, 0.18);
          }
          if (mobileHeader) {
            tl.to([mobileHeader.querySelector("img"), mobileItems], {
              duration: 0.45,
              opacity: 1,
              stagger: 0.06,
              y: 0,
              clearProps: "opacity,transform"
            }, 0.12);
          }
          tl.to(eyebrow, {
            duration: 0.85,
            opacity: 1,
            x: 0
          }, 0.26).to(title, {
            duration: 0.9,
            opacity: 1,
            x: 0
          }, 0.34).to(stepItems, {
            duration: 0.95,
            ease: "power4.out",
            opacity: 1,
            stagger: 0.07,
            x: 0
          }, 0.42);
          if (actionRowItems.length > 0) {
            tl.to(actionRowItems, {
              duration: 0.75,
              ease: "power4.out",
              opacity: 1,
              y: 0
            }, 0.58);
          }
          if (asideArt) {
            gsap.to(asideArt, {
              duration: 4,
              ease: "sine.inOut",
              repeat: -1,
              y: -14,
              yoyo: true
            });
          }
        }, sectionRef);
        cleanup = () => ctx.revert();
      };
      setupAnimation();
      return () => {
        cancelled = true;
        cleanup?.();
      };
    };
    t14 = [];
    $[17] = t13;
    $[18] = t14;
  } else {
    t13 = $[17];
    t14 = $[18];
  }
  useLayoutEffect(t13, t14);
  let t15;
  let t16;
  if ($[19] !== activeStep) {
    t15 = () => {
      if (!hasMountedStepAnimationRef.current) {
        hasMountedStepAnimationRef.current = true;
        return;
      }
      if (shouldReduceMotion()) {
        return;
      }
      let cleanup_0;
      let cancelled_0 = false;
      const setupAnimation_0 = async function setupAnimation() {
        const gsap_0 = await loadGsap();
        if (cancelled_0) {
          return;
        }
        const ctx_0 = gsap_0.context(() => {
          const title_0 = titleRef.current;
          const step_0 = stepContentRef.current;
          const actionRow_0 = actionRowRef.current;
          const asideItems_0 = asideNavRef.current?.querySelectorAll("button") ?? [];
          const mobileItems_0 = mobileHeaderRef.current?.querySelectorAll("button") ?? [];
          if (!title_0 || !step_0) {
            return;
          }
          const stepItems_0 = step_0.querySelectorAll("[data-proposal-animate]");
          const asideActive = asideNavRef.current?.querySelector(`[data-step-index="${activeStep}"]`);
          const mobileActive_0 = mobileHeaderRef.current?.querySelector(`[data-step-index="${activeStep}"]`);
          gsap_0.killTweensOf([title_0, step_0, actionRow_0, ...Array.from(asideItems_0), ...Array.from(mobileItems_0), asideActive, mobileActive_0, ...Array.from(stepItems_0)]);
          gsap_0.set([...Array.from(asideItems_0), ...Array.from(mobileItems_0)], {
            clearProps: "opacity,transform"
          });
          const offscreenRight_0 = getOffscreenRight();
          gsap_0.set(title_0, {
            opacity: 0,
            x: offscreenRight_0
          });
          gsap_0.set(stepItems_0, {
            opacity: 0,
            x: offscreenRight_0,
            willChange: "transform,opacity"
          });
          const tl_0 = gsap_0.timeline({
            defaults: {
              ease: "power3.out"
            }
          });
          tl_0.to(title_0, {
            duration: 0.46,
            opacity: 1,
            x: 0
          }).to(stepItems_0, {
            duration: 0.58,
            ease: "power4.out",
            opacity: 1,
            stagger: 0.05,
            x: 0
          }, 0.06);
          if (actionRow_0) {
            gsap_0.fromTo(actionRow_0, {
              opacity: 0,
              y: -260
            }, {
              duration: 0.72,
              ease: "power4.out",
              opacity: 1,
              y: 0
            });
          }
          if (asideActive) {
            gsap_0.fromTo(asideActive, {
              x: -18,
              opacity: 0.45
            }, {
              duration: 0.42,
              opacity: 1,
              x: 0,
              clearProps: "opacity,transform"
            });
          }
          if (mobileActive_0) {
            gsap_0.fromTo(mobileActive_0, {
              y: 10,
              opacity: 0.55
            }, {
              duration: 0.34,
              opacity: 1,
              y: 0,
              clearProps: "opacity,transform"
            });
          }
        }, sectionRef);
        cleanup_0 = () => ctx_0.revert();
      };
      setupAnimation_0();
      return () => {
        cancelled_0 = true;
        cleanup_0?.();
      };
    };
    t16 = [activeStep];
    $[19] = activeStep;
    $[20] = t15;
    $[21] = t16;
  } else {
    t15 = $[20];
    t16 = $[21];
  }
  useLayoutEffect(t15, t16);
  let t17;
  let t18;
  if ($[22] !== submitError) {
    t17 = () => {
      const error = errorRef.current;
      if (!submitError || !error) {
        return;
      }
      let cancelled_1 = false;
      const animateError = async function animateError() {
        const gsap_1 = await loadGsap();
        if (cancelled_1) {
          return;
        }
        gsap_1.fromTo(error, {
          opacity: 0,
          y: 8
        }, {
          duration: 0.28,
          ease: "power2.out",
          opacity: 1,
          y: 0
        });
      };
      animateError();
      return () => {
        cancelled_1 = true;
      };
    };
    t18 = [submitError];
    $[22] = submitError;
    $[23] = t17;
    $[24] = t18;
  } else {
    t17 = $[23];
    t18 = $[24];
  }
  useLayoutEffect(t17, t18);
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = nextStep => {
      setActiveStep(nextStep);
    };
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  const goToStep = t19;
  let t20;
  if ($[26] !== activeStep || $[27] !== trigger) {
    t20 = async () => {
      const fields = proposalSteps[activeStep]?.fields ?? [];
      const isValid_0 = await trigger([...fields], {
        shouldFocus: true
      });
      if (!isValid_0) {
        return;
      }
      sendStepConversion(proposalSteps[activeStep].key);
      goToStep(Math.min(activeStep + 1, proposalSteps.length - 1));
    };
    $[26] = activeStep;
    $[27] = trigger;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const handleNext = t20;
  let t21;
  if ($[29] !== activeStep || $[30] !== navigate) {
    t21 = async values => {
      setSubmitError(null);
      const response = await fetch("/api/prospects", {
        body: JSON.stringify({
          ...values,
          ...readTrackingData()
        }),
        headers: {
          "Content-Type": "application/json"
        },
        method: "POST"
      });
      if (!response.ok) {
        const data = (await response.json().catch(_temp3)) as {
          message?: string;
        } | null;
        setSubmitError(data?.message ?? "Desculpe, estamos voltando em alguns minutos =)");
        return;
      }
      sendStepConversion(proposalSteps[activeStep].key);
      navigate("/solicitacao-de-proposta-enviada");
    };
    $[29] = activeStep;
    $[30] = navigate;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== handleSubmit || $[33] !== t21) {
    t22 = handleSubmit(t21);
    $[32] = handleSubmit;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  const onSubmit = t22;
  let t23;
  if ($[35] !== activeStep || $[36] !== budgetOptions || $[37] !== control || $[38] !== errors || $[39] !== register) {
    t23 = () => {
      switch (activeStep) {
        case 0:
          {
            return <ProposalStepPersonal control={control} errors={errors} register={register} />;
          }
        case 1:
          {
            return <ProposalStepCompany control={control} errors={errors} register={register} />;
          }
        case 2:
          {
            return <ProposalStepType control={control} />;
          }
        case 3:
          {
            return <ProposalStepBudget budgetOptions={budgetOptions} control={control} />;
          }
        case 4:
          {
            return <ProposalStepDetails errors={errors} register={register} />;
          }
        default:
          {
            return null;
          }
      }
    };
    $[35] = activeStep;
    $[36] = budgetOptions;
    $[37] = control;
    $[38] = errors;
    $[39] = register;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  const renderActiveStep = t23;
  let t24;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <a href="/"><Image alt="Flow Lab Tech" className="absolute left-52 top-21 h-auto w-full max-w-47.5" height={31} ref={asideLogoRef} priority={true} src="/assets/logo-flow.png" width={190} /></a>;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== activeStep) {
    t25 = <div className="relative z-10 h-full w-full">{t24}<nav ref={asideNavRef} className="absolute left-52 top-52.75 flex w-full max-w-51.75 flex-col gap-4"><ProposalStepper activeStep={activeStep} onStepChange={goToStep} /></nav></div>;
    $[42] = activeStep;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Image alt="" className="pointer-events-none absolute bottom-0 left-0 h-auto w-full max-w-127 object-cover" height={614} priority={true} ref={asideArtRef} src="/assets/propose.png" width={508} />;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== t25) {
    t27 = <aside ref={asideRef} className="relative hidden h-screen w-full max-w-lg shrink-0 overflow-hidden bg-black lg:block">{t25}{t26}</aside>;
    $[45] = t25;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <a href="/" aria-label="Flowlab Tech"><Image alt="Flow Lab Tech" className="h-auto w-full max-w-40" height={31} priority={true} src="/assets/logo-flow.png" width={190} /></a>;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== activeStep) {
    t29 = <div ref={mobileHeaderRef} className="flex w-full flex-col items-center justify-center lg:hidden">{t28}<div className="-mx-6 mt-8 w-full overflow-x-auto px-6 sm:-mx-10 sm:px-10 pb-3"><div className="flex w-max min-w-full items-center gap-x-4 gap-y-3"><ProposalStepper activeStep={activeStep} mobile={true} onStepChange={goToStep} /></div></div></div>;
    $[48] = activeStep;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="pt-12 lg:pt-0"><p ref={eyebrowRef} className="font-bricolage text-xl md:text-[24px] leading-[1.2] font-medium text-[#5c5c5c]">Solicitar orçamento</p></div>;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  const t31 = proposalSteps[activeStep];
  const t32 = sectionStepTitles[activeStep];
  let t33;
  if ($[51] !== t32) {
    t33 = <div className="overflow-hidden"><h2 ref={titleRef} className="font-bricolage text-[20px] leading-[1.4] font-normal text-white">{t32}</h2></div>;
    $[51] = t32;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== renderActiveStep) {
    t34 = renderActiveStep();
    $[53] = renderActiveStep;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  if ($[55] !== t31.key || $[56] !== t33 || $[57] !== t34) {
    t35 = <div ref={stepContentRef} className="space-y-6" key={t31.key}>{t33}{t34}</div>;
    $[55] = t31.key;
    $[56] = t33;
    $[57] = t34;
    $[58] = t35;
  } else {
    t35 = $[58];
  }
  let t36;
  if ($[59] !== activeStep || $[60] !== handleNext || $[61] !== isSubmitting || $[62] !== isValid) {
    t36 = <div ref={actionRowRef} className="mt-12 flex flex-wrap items-center gap-3">{activeStep === proposalSteps.length - 1 ? <ProposalActionButton disabled={isSubmitting || !isValid} key={`proposal-action-${activeStep}`} loading={isSubmitting} submit={true} text="Enviar" /> : <ProposalActionButton disabled={isSubmitting} key={`proposal-action-${activeStep}`} loading={isSubmitting} onClick={handleNext} text={"Avan\xE7ar"} />}</div>;
    $[59] = activeStep;
    $[60] = handleNext;
    $[61] = isSubmitting;
    $[62] = isValid;
    $[63] = t36;
  } else {
    t36 = $[63];
  }
  let t37;
  if ($[64] !== submitError) {
    t37 = submitError && <p ref={errorRef} className="mt-4 text-sm text-red-300">{submitError}</p>;
    $[64] = submitError;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] !== onSubmit || $[67] !== t35 || $[68] !== t36 || $[69] !== t37) {
    t38 = <form className="mt-12 flex flex-1 flex-col lg:mt-24" onSubmit={onSubmit}>{t35}{t36}{t37}</form>;
    $[66] = onSubmit;
    $[67] = t35;
    $[68] = t36;
    $[69] = t37;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== t29 || $[72] !== t38) {
    t39 = <div className="flex min-w-0 flex-1 bg-gray-1000 px-6 py-10 text-white sm:px-10 lg:px-0 lg:py-0"><div className="w-full"><div className="mx-auto flex h-full max-w-127.25 flex-col lg:pt-22">{t29}{t30}{t38}</div></div></div>;
    $[71] = t29;
    $[72] = t38;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== t27 || $[75] !== t39) {
    t40 = <section ref={sectionRef} className="propose-screen min-h-screen bg-gray-1000 text-white"><div className="flex min-h-screen w-full overflow-hidden bg-gray-1000">{t27}{t39}</div></section>;
    $[74] = t27;
    $[75] = t39;
    $[76] = t40;
  } else {
    t40 = $[76];
  }
  return t40;
}
function _temp3() {
  return null;
}
function _temp2() {
  if (typeof window === "undefined") {
    return 600;
  }
  return Math.min(window.innerWidth, 900);
}
function _temp() {
  if (typeof window === "undefined") {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  for (const key of trackingKeys) {
    const value = params.get(key);
    if (value) {
      sessionStorage.setItem(key, value);
    }
  }
  const origin = params.get("origin") ?? sessionStorage.getItem("origin");
  sessionStorage.setItem("origin", origin || "Site Flowlab");
}